import { AlertTriangle, Pencil, RotateCcw } from "lucide-react";
import type { ReactNode } from "react";
import type { PlanStop } from "./canvasModel";

interface PlanOutcomeProps {
  phase: PlanStop;
  /** Names the box or section in accessible labels, e.g. "Box 2". */
  label: string;
  question: string;
  message: string;
  options: string[];
  candidates: Array<{ title: string }>;
  retryable: boolean;
  onChooseOption: (option: string) => void;
  onKeepCandidate: (index: number) => void;
  onRetry: () => void;
  onEdit: () => void;
}

/**
 * What a canvas shows when a question did not plan straight into a chart:
 * a clarifying choice, several readings to pick from, a refusal, or a failure.
 * Shared by the dashboard boxes and the document rows so both behave alike.
 */
export function PlanOutcome({
  phase,
  label,
  question,
  message,
  options,
  candidates,
  retryable,
  onChooseOption,
  onKeepCandidate,
  onRetry,
  onEdit,
}: PlanOutcomeProps) {
  if (phase === "clarify") {
    return (
      <Prompt message={message} question={question}>
        <div className="canvas-tile-options">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className="button secondary small"
              onClick={() => onChooseOption(option)}
            >
              {option}
            </button>
          ))}
        </div>
        <EditButton label={label} onEdit={onEdit} />
      </Prompt>
    );
  }
  if (phase === "multiple") {
    return (
      <Prompt message={message} question={question}>
        <div className="canvas-tile-options">
          {candidates.map((candidate, index) => (
            <button
              key={`${candidate.title}-${index}`}
              type="button"
              className="button secondary small"
              onClick={() => onKeepCandidate(index)}
            >
              Keep “{candidate.title}”
            </button>
          ))}
        </div>
        <EditButton label={label} onEdit={onEdit} />
      </Prompt>
    );
  }
  if (phase === "unsupported") {
    return (
      <Prompt message={message} question={question}>
        <EditButton label={label} onEdit={onEdit} primary />
      </Prompt>
    );
  }
  return (
    <Prompt message={message} question={question} tone="error">
      <div className="canvas-tile-options">
        {retryable && (
          <button
            type="button"
            className="button primary small"
            onClick={onRetry}
            aria-label={`Retry ${label}`}
          >
            <RotateCcw size={14} /> Retry
          </button>
        )}
        <EditButton label={label} onEdit={onEdit} />
      </div>
    </Prompt>
  );
}

function Prompt({
  message,
  question,
  tone = "info",
  children,
}: {
  message: string;
  question: string;
  tone?: "info" | "error";
  children: ReactNode;
}) {
  return (
    <div className={`canvas-tile-prompt ${tone}`} role={tone === "error" ? "alert" : undefined}>
      {tone === "error" && <AlertTriangle size={18} aria-hidden="true" />}
      <p className="canvas-tile-question">“{question}”</p>
      <p>{message}</p>
      {children}
    </div>
  );
}

function EditButton({
  label,
  onEdit,
  primary = false,
}: {
  label: string;
  onEdit: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      className={`button ${primary ? "primary" : "tertiary"} small`}
      onClick={onEdit}
      aria-label={`Edit the question in ${label}`}
    >
      <Pencil size={13} /> Edit
    </button>
  );
}
